import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { confirmDialog, promptDialog } from "@/lib/dialogs";
import { Upload } from "lucide-react";

const BK = process.env.REACT_APP_BACKEND_URL;

export default function ImagePicker({ open, onOpenChange, onSelect }) {
  const [media, setMedia] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/media").then((r) => setMedia(r.data)).catch(() => {});
  useEffect(() => { if (open) load(); }, [open]);

  const del = async (e, m) => {
    e.stopPropagation();
    if (!(await confirmDialog({ title: "Remove image?", description: "Remove this image from the library? This cannot be undone.", tone: "destructive", confirmText: "Remove" }))) return;
    try { await api.delete(`/media/${m.id}`); load(); } catch { toast.error("Could not remove"); }
  };
  const rename = async (e, m) => {
    e.stopPropagation();
    const n = await promptDialog({ title: "Rename image", label: "Image name", defaultValue: m.filename || "", confirmText: "Rename" });
    if (n == null) return;
    try { await api.put(`/media/${m.id}`, { filename: n }); load(); } catch { toast.error("Could not rename"); }
  };

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd);
      toast.success("Image uploaded");
      onSelect(data.path, `${BK}/api/public/files/${data.path}`);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="image-picker">
        <DialogHeader><DialogTitle>Choose an image</DialogTitle><DialogDescription>Upload a new image or pick one from your library.</DialogDescription></DialogHeader>
        <label className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded border border-border cursor-pointer hover:bg-cass-bone w-max" data-testid="image-picker-upload">
          <Upload className="w-4 h-4" />{busy ? "Uploading…" : "Upload new image"}
          <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
        </label>
        <p className="text-xs text-cass-slate mt-3 mb-1">Your image library — click to re-use</p>
        {media.length === 0 ? (
          <p className="text-sm text-cass-slate">No images yet — upload one above and it'll be saved here for re-use.</p>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
            {media.map((m) => (
              <button key={m.path} type="button"
                onClick={() => { onSelect(m.path, `${BK}${m.url}`); onOpenChange(false); }}
                className="relative aspect-square rounded-lg border border-border overflow-hidden hover:ring-2 ring-cass-teal transition-all group"
                title={m.filename || ""} data-testid="media-item">
                <img src={`${BK}${m.url}`} alt={m.filename || ""} className="w-full h-full object-cover" loading="lazy" />
                <span className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <span role="button" tabIndex={0} onClick={(e) => rename(e, m)} className="w-6 h-6 grid place-items-center rounded bg-white/90 text-cass-navy hover:bg-white text-xs" title="Rename" data-testid="media-rename">✎</span>
                  <span role="button" tabIndex={0} onClick={(e) => del(e, m)} className="w-6 h-6 grid place-items-center rounded bg-white/90 text-red-600 hover:bg-white text-xs" title="Remove" data-testid="media-delete">✕</span>
                </span>
              </button>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
